import { useEffect, useState } from 'react';
import { AppState, NativeModules, Platform, useWindowDimensions } from 'react-native';

export type AndroidSystemBarInsets = {
  bottom: number;
  left: number;
  navigationBarsBottom: number;
  right: number;
  systemBarsBottom: number;
  systemGesturesBottom: number;
  tappableElementBottom: number;
  top: number;
};

type AndroidSystemBarsNativeModule = {
  getInsets?: () => Promise<Partial<AndroidSystemBarInsets>>;
};

export const EMPTY_ANDROID_SYSTEM_BAR_INSETS: AndroidSystemBarInsets = {
  bottom: 0,
  left: 0,
  navigationBarsBottom: 0,
  right: 0,
  systemBarsBottom: 0,
  systemGesturesBottom: 0,
  tappableElementBottom: 0,
  top: 0,
};

const AndroidSystemBars = NativeModules.AndroidSystemBars as AndroidSystemBarsNativeModule;

function normalizeInsets(insets: Partial<AndroidSystemBarInsets>): AndroidSystemBarInsets {
  return {
    ...EMPTY_ANDROID_SYSTEM_BAR_INSETS,
    ...insets,
  };
}

async function getAndroidSystemBarInsets(): Promise<AndroidSystemBarInsets> {
  if (Platform.OS !== 'android' || !AndroidSystemBars?.getInsets) {
    return EMPTY_ANDROID_SYSTEM_BAR_INSETS;
  }

  try {
    return normalizeInsets(await AndroidSystemBars.getInsets());
  } catch {
    return EMPTY_ANDROID_SYSTEM_BAR_INSETS;
  }
}

export default function useAndroidSystemBarInsets(): AndroidSystemBarInsets {
  const [insets, setInsets] = useState(EMPTY_ANDROID_SYSTEM_BAR_INSETS);
  const { height, width } = useWindowDimensions();

  useEffect(() => {
    let isMounted = true;

    getAndroidSystemBarInsets().then((nextInsets) => {
      if (isMounted) {
        setInsets(nextInsets);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [height, width]);

  useEffect(() => {
    if (Platform.OS !== 'android') {
      return undefined;
    }

    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        getAndroidSystemBarInsets().then(setInsets);
      }
    });

    return () => subscription.remove();
  }, []);

  return insets;
}
